import { useSuspenseQuery } from '@apollo/client';
import { Suspense, useState, useTransition } from 'react';
import { useIntl } from 'react-intl';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import {
  LazyLoading,
  LAZY_LOADING_TYPE_BUTTON,
  EmptyState,
  WarningMessageBox as Error,
} from '@xing-com/crate-entity-pages-common';
import {
  EmployeesFencingView,
  createEmployeeCards,
  EmployeesGrid,
  EmployeesDocument,
  LIMIT_EMPLOYEES_LIST,
  LIMIT_EMPLOYEES_LIST_LOGGED_OUT,
  QUERY_CONSUMER_DETAIL,
  EMPLOYEES_SORTING,
  useNetworkRequestLoggedOutAction,
} from '@xing-com/crate-entity-pages-employees';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';

import * as Styled from './employees-detail.styles';

const i18nKeys = {
  single: 'EP_EMPLOYEE',
  multiple: 'EP_EMPLOYEES',
};

const EmployeesDetailInner: React.FC = () => {
  const { $t } = useIntl();
  const { isLoggedIn } = useLoginState();
  const { pageContext } = usePageContext();
  const [page, setPage] = useState(0);
  const [isPending, startTransition] = useTransition();
  const { data, error, fetchMore, refetch } = useSuspenseQuery(
    EmployeesDocument,
    {
      variables: {
        id: pageContext.companyId ?? '',
        first: isLoggedIn
          ? LIMIT_EMPLOYEES_LIST
          : LIMIT_EMPLOYEES_LIST_LOGGED_OUT,
        query: { consumer: QUERY_CONSUMER_DETAIL, sort: EMPLOYEES_SORTING },
      },
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    }
  );

  const total = data?.company?.employees?.total;
  const employeesEdged = data?.company?.employees?.edges ?? [];
  const employeesNodes = employeesEdged
    .map((edge) => edge?.node)
    .filter((node) => !!node);

  useNetworkRequestLoggedOutAction({
    employees: employeesNodes,
  });

  if (error) return <ErrorFallback onReload={refetch} />;

  const employeesCards = createEmployeeCards(employeesNodes);
  const totalEmployeeKey = total === 1 ? i18nKeys.single : i18nKeys.multiple;

  const handleOnLazyLoad = async () => {
    const endCursor = data?.company?.employees?.pageInfo.endCursor;

    startTransition(() => {
      fetchMore({
        variables: {
          id: pageContext.companyId ?? '',
          first: LIMIT_EMPLOYEES_LIST,
          after: endCursor,
          query: {
            consumer: QUERY_CONSUMER_DETAIL,
            sort: EMPLOYEES_SORTING,
          },
        },
        updateQuery: (prev: any, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          return {
            ...prev,
            company: {
              ...prev?.company,
              employees: {
                ...prev?.company?.employees,
                edges: [
                  ...(prev?.company?.employees?.edges || []),
                  ...(fetchMoreResult?.company?.employees?.edges || []),
                ],
                pageInfo: fetchMoreResult?.company?.employees?.pageInfo,
              },
            },
          };
        },
      }).then(() => {
        setPage(page + 1);
      });
    });
  };

  const handleOnAddContact = () => {
    refetch({
      id: pageContext.companyId ?? '',
      first: LIMIT_EMPLOYEES_LIST * (page + 1),
      query: {
        consumer: QUERY_CONSUMER_DETAIL,
        sort: EMPLOYEES_SORTING,
      },
    });
  };

  const hasNextPage = data?.company?.employees?.pageInfo.hasNextPage;
  const isPaginationEnabled = hasNextPage === true;

  if (total === 0) {
    return (
      <EmptyState
        headlineCopyKey="EP_EMPLOYEES_NUMBER_EMPTY_STATE"
        textCopyKey="EP_EMPLOYEES_EMPTY_STATE"
        statusCode={isLoggedIn ? undefined : 404}
      />
    );
  }

  const isFenced =
    !isLoggedIn &&
    total &&
    (total > LIMIT_EMPLOYEES_LIST_LOGGED_OUT ||
      (employeesCards?.length ?? 0) > LIMIT_EMPLOYEES_LIST_LOGGED_OUT);

  return (
    <>
      {total && (
        <Styled.TotalEmployees>{`${total} ${$t({ id: totalEmployeeKey })}`}</Styled.TotalEmployees>
      )}
      <>
        {isFenced ? (
          <EmployeesFencingView employeesCards={employeesCards} total={total} />
        ) : (
          <EmployeesGrid
            employeesCards={employeesCards}
            trackingModuleType="Subpage"
            onAddContact={handleOnAddContact}
          />
        )}
      </>
      {isLoggedIn && (
        <LazyLoading
          type={LAZY_LOADING_TYPE_BUTTON}
          isLoading={isPending}
          enabled={isPaginationEnabled}
          onLazyLoad={handleOnLazyLoad}
          ctaText="EP_EMPLOYEES_LOAD_MORE_CTA"
        />
      )}
    </>
  );
};

const Fallback: React.FC = () => {
  return <EmployeesGrid.Skeleton />;
};

const ErrorFallback: React.FC<{
  onReload?: () => void;
}> = ({ onReload = () => window.location.reload() }) => {
  return (
    <div data-testid="errorContainer">
      <Error
        headerText="EP_ERROR_HEADER"
        bodyText="EP_ERROR_BODY"
        buttonText="EP_ERROR_RELOAD_CTA"
        onClick={onReload}
      />
    </div>
  );
};

export const EmployeesDetail: React.FC = () => {
  return (
    <ErrorBoundary onError={() => <ErrorFallback />}>
      <Suspense fallback={<Fallback />}>
        <EmployeesDetailInner />
      </Suspense>
    </ErrorBoundary>
  );
};
